<template>
  <div class="node-tools-bar d-flex flex-column" style="" data-cy="node-tools-bar">
    <!-- The breadcrumb component on the responsive view -->
    <div v-if="$vuetify.display.xs" class="second-top-bar px-2" style="">
      <breadcrumb v-if="!showingBackToFilesButton"/>

      <div v-if="showingBackToFilesButton" class="text-center">
        <v-btn
          v-if="getCurrentNode"
          :to="'/node/' + getCurrentNode.id"
          color="primary"
          class="mx-1 mt-1"
          variant="flat"
          size="x-small">
          <v-icon>mdi-keyboard-backspace</v-icon>
          {{ $t('layouts.main.backToFiles') }}
        </v-btn>
      </div>
    </div>

    <div class="d-flex flex-row">
      <div v-if="$canUploadFileAndAsset()" class="mr-1" style="flex-grow: 4;">
        <UploadFilesDialog v-if="getCurrentNode" :node="getCurrentNode"
                           :disabled="isCurrentNodeArchived" v-model="showUploadFilesDialog">
          <template v-slot:activator="{ props }">
            <v-btn
              v-bind="props"
              color="primary"
              :disabled="isCurrentNodeArchived"
              :size="$vuetify.display.smAndDown ? 'small' : 'default'"
              density="comfortable"
              block
              variant="flat"
              data-cy="node-tools-bar-uploadButton"
            >
              <v-icon>mdi-cloud-upload</v-icon>
              <span v-if="$vuetify.display.mdAndUp">
              {{ $t('routes.node.upload') }}
            </span>
            </v-btn>
          </template>
        </UploadFilesDialog>
      </div>

      <div class="mr-1" style="flex-grow: 4;" v-if="canListAccesslinks || canControlAccesslinks">
        <v-menu v-model="showingShareMenu" :disabled="isCurrentNodeArchived" eager>
          <template v-slot:activator="{ props }">
            <v-btn
              v-bind="props"
              color="secondaryButtons"
              :size="$vuetify.display.smAndDown ? 'small' : 'default'"
              :disabled="isCurrentNodeArchived"
              variant="flat"
              density="comfortable"
              block
              data-cy="node-tools-bar-shareButton"
            >
              <v-icon>mdi-share</v-icon>
              <span v-if="$vuetify.display.mdAndUp">
                {{ $t('common.share') }}
              </span>
              <v-icon>mdi-menu-down</v-icon>
            </v-btn>
          </template>

          <v-list data-cy="node-tools-bar-share-list">
            <v-list-item
              v-if="canControlAccesslinks && getCurrentNode"
              @click="openShareSelectedDialog"
              data-cy="node-tools-bar-share-shareSelected"
              prepend-icon="mdi-share-circle"
            >
              <v-list-item-title>
                {{ $t('routes.node.shareFiles', getSelectedPods.length) }}
              </v-list-item-title>
            </v-list-item>

            <v-list-item
              v-if="canListAccesslinks && getCurrentNode"
              @click="openAccessLinksListDialog"
              data-cy="node-tools-bar-share-accessLinks"
              prepend-icon="mdi-share-all"
            >
              <v-list-item-title>
                {{ $t('routes.node.listSharedFiles') }}
              </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>

        <!-- Standalone dialogs for Access Links -->
        <AccesslinkCreateDialog
          v-if="canControlAccesslinks && getCurrentNode"
          v-model="showShareSelectedDialog"
          :rootNode="getCurrentNode"
          :selectedPods="getSelectedPods"
          :selectedNodes="getSelectedNodes"
          ref="accesslinkCreateDialog" />

        <AccesslinksListDialog
          v-if="canListAccesslinks && getCurrentNode"
          v-model="showAccessLinksListDialog"
          :node="getCurrentNode"
          ref="accesslinksListDialog" />
      </div>

      <div class="mr-1" style="flex-grow: 4;">
        <FilesContextMenu v-model="showFilesContextMenu">
          <template v-slot:activator="{ props }">
            <v-btn
              v-bind="props"
              color="secondaryButtons"
              :size="$vuetify.display.smAndDown ? 'small' : 'default'"
              variant="flat"
              density="comfortable"
              block
              data-cy="node-tools-bar-filesButton"
            >
              <v-icon>mdi-file</v-icon>
              <span v-if="$vuetify.display.mdAndUp">
                {{ $t('routes.node.files') }}
              </span>
              <v-icon>mdi-menu-down</v-icon>
            </v-btn>
          </template>
        </FilesContextMenu>
      </div>

      <div v-if="canListUploadlinks || canControlUploadlinks"
           class="mr-1"
           :disabled="isCurrentNodeArchived"
           style="flex-grow: 4;"
      >
        <v-menu v-model="showingRequestsMenu" eager>
          <template v-slot:activator="{ props }">
            <v-btn
              v-bind="props"
              color="primary"
              :disabled="isCurrentNodeArchived"
              :size="$vuetify.display.smAndDown ? 'small' : 'default'"
              variant="flat"
              density="comfortable"
              block
              data-cy="node-tools-bar-requestButton"
            >
              <v-icon>mdi-file-arrow-up-down</v-icon>
              <span v-if="$vuetify.display.mdAndUp">
                  {{ $t('routes.node.request') }}
                </span>
              <v-icon>mdi-menu-down</v-icon>
            </v-btn>
          </template>

          <v-list>
            <v-list-item
              v-if="canControlUploadlinks && getCurrentNode"
              @click="openRequestFilesDialog"
              data-cy="node-tools-bar-request-requestFiles"
              prepend-icon="mdi-text-box-plus"
            >
              <v-list-item-title>
                {{ $t('routes.node.requestFiles') }}
              </v-list-item-title>
            </v-list-item>

            <v-list-item
              v-if="canListUploadlinks && getCurrentNode"
              @click="openUploadLinksListDialog"
              data-cy="node-tools-bar-request-listRequests"
              prepend-icon="mdi-text-box-multiple"
            >
              <v-list-item-title>
                {{ $t('routes.node.listRequests') }}
              </v-list-item-title>
            </v-list-item>

            <v-divider
              v-if="canControlUploadlinks && getCurrentNode"
              data-cy="node-tools-bar-request-divider"
            />

            <v-list-item
              v-if="canControlUploadlinks && getCurrentNode"
              @click="openGuidedRecordingDialog"
              data-cy="node-tools-bar-request-guidedRecording"
              prepend-icon="mdi-video-plus-outline"
              :aria-label="$t('routes.guidedRecording.requestMenuItem')"
            >
              <v-list-item-title>
                {{ $t('routes.guidedRecording.requestMenuItem') }}
              </v-list-item-title>
            </v-list-item>

            <v-list-item
              v-if="canControlUploadlinks && getCurrentNode"
              @click="openGuidedRecordingInvitationsListDialog"
              data-cy="node-tools-bar-request-guidedRecordingInvitations"
              prepend-icon="mdi-clipboard-text-outline"
            >
              <v-list-item-title>
                {{ $t('routes.guidedRecording.invitationsTitle') }}
              </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>

        <!-- Standalone dialogs -->
        <RequestFilesDialog
          v-if="canControlUploadlinks && getCurrentNode"
          v-model="showRequestFilesDialog"
          ref="requestFilesDialog" />

        <GuidedRecordingDialog
          v-if="canControlUploadlinks && getCurrentNode"
          v-model="showGuidedRecordingDialog"
          :node="getCurrentNode"
          :selected-invitation="selectedGuidedRecordingInvitation"
        />

        <GuidedRecordingInvitationsListDialog
          v-if="canControlUploadlinks && getCurrentNode"
          v-model="showGuidedRecordingInvitationsListDialog"
          :node="getCurrentNode"
          @create="openGuidedRecordingDialogFromInvitations"
          @edit="openGuidedRecordingInvitationFromInvitations"
        />

        <UploadlinksListDialog
          v-if="canListUploadlinks && getCurrentNode"
          v-model="showUploadlinksListDialog"
          ref="uploadLinksListDialog" />
      </div>

      <SearchForm>
        <template v-slot:activator="{ props: activatorProps }">
          <v-tooltip location="bottom" :z-index="1200" :aria-label="$t('menus.topBars.search')">
            <template v-slot:activator="{ props }">
              <v-btn
                v-bind="{ ...props, ...activatorProps }"
                :size="$vuetify.display.smAndDown ? 'small' : 'default'"
                variant="flat"
                density="default"
                color="transparent"
                class="mr-1"
                style="flex-grow: 0; border-radius: 10px;"
                icon
                :aria-label="$t('menus.topBars.search')"
              >
                <v-icon>mdi-magnify</v-icon>
              </v-btn>
            </template>
            <span>{{ $t('menus.topBars.search') }}</span>
          </v-tooltip>
        </template>
      </SearchForm>

      <FilterForm>
        <template v-slot:activator="{ hasAppliedFilters, props: activatorProps }">
          <v-tooltip location="bottom" :z-index="1200" :aria-label="$t('menus.topBars.filter')">
            <template v-slot:activator="{ props }">
              <v-btn
                v-bind="{...props, ...activatorProps}"
                :size="$vuetify.display.smAndDown ? 'small' : 'default'"
                variant="flat"
                density="default"
                :color="hasAppliedFilters ? 'primary' : 'transparent'"
                class="mr-1"
                style="flex-grow: 0; border-radius: 10px;"
                icon
                :aria-label="$t('menus.topBars.filter')"
              >
                <v-icon>mdi-filter-variant</v-icon>
              </v-btn>
            </template>
            <span>{{ $t('menus.topBars.filter') }}</span>
          </v-tooltip>
        </template>
      </FilterForm>

      <v-tooltip
        location="bottom"
        :aria-label="getNodeView === 'list' ? $t('menus.topBars.viewAsGrid') : $t('menus.topBars.viewAsList')"
      >
        <template v-slot:activator="{ props }">
          <v-btn
            v-bind="props"
            :size="$vuetify.display.smAndDown ? 'small' : 'default'"
            @click="toggleNodeView"
            variant="flat"
            density="default"
            color="transparent"
            class="mr-1"
            style="flex-grow: 0; border-radius: 10px;"
            icon
            data-cy="node-tools-bar-toggleNodeView"
            :aria-label="getNodeView === 'list' ? $t('menus.topBars.viewAsGrid') : $t('menus.topBars.viewAsList')"
          >
            <v-icon v-if="getNodeView === 'list'" class="mr-0" size="20px">mdi-view-module</v-icon>
            <v-icon v-if="getNodeView === 'grid'" class="mr-0" size="20px">mdi-view-list</v-icon>
          </v-btn>
        </template>
        <span v-if="getNodeView === 'list'">{{ $t('menus.topBars.viewAsGrid') }}</span>
        <span v-else>{{ $t('menus.topBars.viewAsList') }}</span>
      </v-tooltip>
    </div>
  </div>
</template>

<script>
import { usePodStore } from '@/stores/PodStore.js';
import { useNodeStore } from '@/stores/NodeStore.js';
import FilterForm from './FilterForm.vue';
import SearchForm from './SearchForm.vue';
import FilesContextMenu from '../contextMenus/FilesContextMenu.vue';
import UploadFilesDialog from '../dialogs/UploadFilesDialog.vue';
import RequestFilesDialog from '../dialogs/RequestFilesDialog.vue';
import AccesslinkCreateDialog from '../dialogs/AccesslinkCreateDialog.vue';
import AccesslinksListDialog from '../dialogs/AccesslinksListDialog.vue';
import UploadlinksListDialog from '../dialogs/UploadlinksListDialog.vue';
import GuidedRecordingDialog from '../dialogs/GuidedRecordingDialog.vue';
import GuidedRecordingInvitationsListDialog from '../dialogs/GuidedRecordingInvitationsListDialog.vue';
import Breadcrumb from '@/layouts2/mainLayout/components/topBars/Breadcrumb.vue';

export default {
  name: 'NodeToolsBar',
  components: {
    Breadcrumb,
    FilterForm,
    SearchForm,
    RequestFilesDialog,
    UploadFilesDialog,
    FilesContextMenu,
    AccesslinkCreateDialog,
    AccesslinksListDialog,
    UploadlinksListDialog,
    GuidedRecordingDialog,
    GuidedRecordingInvitationsListDialog,
  },
  data() {
    return {
      searchText: '',
      showingShareMenu: false,
      showingRequestsMenu: false,
      showRequestFilesDialog: false,
      showGuidedRecordingDialog: false,
      showGuidedRecordingInvitationsListDialog: false,
      selectedGuidedRecordingInvitation: null,
      showUploadlinksListDialog: false,
      showShareSelectedDialog: false,
      showAccessLinksListDialog: false,
      showFilesContextMenu: false,
      showUploadFilesDialog: false,
    };
  },
  methods: {
    stub() {
      this.showingShareMenu = false;
      this.showingRequestsMenu = false;
    },
    openShareSelectedDialog() {
      this.showingShareMenu = false;
      this.$nextTick(() => {
        this.showShareSelectedDialog = true;
      });
    },
    openAccessLinksListDialog() {
      this.showingShareMenu = false;
      this.$nextTick(() => {
        this.showAccessLinksListDialog = true;
      });
    },
    openRequestFilesDialog() {
      this.showingRequestsMenu = false;
      this.$nextTick(() => {
        this.showRequestFilesDialog = true;
      });
    },
    openUploadLinksListDialog() {
      this.showingRequestsMenu = false;
      this.$nextTick(() => {
        this.showUploadlinksListDialog = true;
      });
    },
    openGuidedRecordingDialog() {
      this.showingRequestsMenu = false;
      this.selectedGuidedRecordingInvitation = null;
      this.$nextTick(() => {
        this.showGuidedRecordingDialog = true;
      });
    },
    openGuidedRecordingInvitationsListDialog() {
      this.showingRequestsMenu = false;
      this.$nextTick(() => {
        this.showGuidedRecordingInvitationsListDialog = true;
      });
    },
    openGuidedRecordingDialogFromInvitations() {
      this.showGuidedRecordingInvitationsListDialog = false;
      this.selectedGuidedRecordingInvitation = null;
      this.$nextTick(() => {
        this.showGuidedRecordingDialog = true;
      });
    },
    openGuidedRecordingInvitationFromInvitations(invitation) {
      if (!invitation?.id || !invitation?.projectnode?.id) {
        return;
      }

      this.showGuidedRecordingInvitationsListDialog = false;
      this.selectedGuidedRecordingInvitation = invitation;
      this.$nextTick(() => {
        this.showGuidedRecordingDialog = true;
      });
    },
    toggleNodeView() {
      window.scrollTo(0, 0);

      // store the value of folder view after toggle.
      const nodeStore = useNodeStore();
      nodeStore.toggleNodeView();
    },
  },
  computed: {
    getCurrentNode() {
      const nodeStore = useNodeStore();
      return nodeStore.getCurrentNode;
    },
    isCurrentNodeArchived() {
      return Boolean(this.getCurrentNode && this.getCurrentNode.archived);
    },
    getNodeView() {
      const nodeStore = useNodeStore();
      return nodeStore.getNodeView;
    },
    getSelectedPods() {
      const podStore = usePodStore();
      return podStore.getSelectedPods;
    },
    getSelectedNodes() {
      const nodeStore = useNodeStore();
      return nodeStore.getSelectedNodes;
    },
    canListAccesslinks() {
      return this.$canListNodeAndPod() &&
        this.$canListShareLinks();
    },
    canControlAccesslinks() {
      return this.$canListNodeAndPod() &&
        this.$canControlNodeAndPod() &&
        this.$canListShareLinks() &&
        this.$canControlShareLinks();
    },
    canListUploadlinks() {
      return this.$canListNodeAndPod() &&
        this.$canControlNodeAndPod() &&
        this.$canUploadFileAndAsset() &&
        this.$canControlPodService() &&
        this.$canListShareLinks();
    },
    canControlUploadlinks() {
      return this.$canListNodeAndPod() &&
        this.$canControlNodeAndPod() &&
        this.$canUploadFileAndAsset() &&
        this.$canControlPodService() &&
        this.$canListShareLinks() &&
        this.$canControlShareLinks();
    },
    showingBackToFilesButton() {
      return this.$route.path.startsWith('/recycle-bin');
    },
  }
};
</script>

<style>
.node-tools-bar {
  position: sticky;
  top: 0;
  padding: 12px 0 8px 0;
  z-index: 4;
  overflow: auto;
  background-color: rgb(var(--v-theme-background));
}

@media only screen and (max-width: 600px) {
  .node-tools-bar {
    padding-top: 0;
  }
}

#node-search-input .v-input__slot {
  background-color: white !important;
}
</style>
